/**
 * The pure half of the continuous ("always listening") capture path in
 * {@link import('./audio-recorder')}: a fixed-size PCM ring buffer, the
 * sample-index arithmetic around it, and a WAV encoder.
 *
 * Why this exists at all: the therapist web app records with an explicit
 * tap-to-start / tap-to-stop, and both taps are late relative to the speaker.
 * People start the word before the browser has the mic running, and they hit
 * stop while the last phoneme is still trailing off — the scorer then sees a
 * clipped word and reports deletions that the patient never made. The fix is
 * to hold the microphone open for the whole exercise, keep the last few
 * seconds of audio in memory, and cut the clip from *before* the start tap to
 * *after* the stop tap. That requires random access into recent audio, which
 * `MediaRecorder` cannot give us (its webm/mp4 chunks aren't independently
 * decodable), so we buffer raw PCM ourselves and encode a WAV at cut time —
 * a container the scorer's ffmpeg reads and the proxy already allows.
 *
 * Everything here is deliberately free of Web Audio so the cut arithmetic (the
 * part that is easy to get subtly wrong) is unit-testable in jsdom. The Web
 * Audio glue lives in `audio-recorder.ts`.
 */

/**
 * A fixed-capacity ring of mono float samples addressed by an ABSOLUTE sample
 * index — the count of samples written since capture began. Absolute indices
 * are the whole point: the recorder records "the tap happened at sample N" and
 * later asks for `[N - preRoll, M + postRoll)` without having to track where
 * that landed in the physical buffer, or whether it has been overwritten since.
 */
export interface RingBuffer {
  /** Samples retained. Older audio is dropped as new audio arrives. */
  readonly capacity: number
  /** Total samples ever written — the absolute timeline's current end. */
  written(): number
  /** Oldest absolute index still retained (`written() - capacity`, floored). */
  oldest(): number
  /** Append a frame. Frames longer than `capacity` keep only their tail. */
  write(frame: Float32Array): void
  /**
   * Copy the absolute half-open range `[from, to)`, clamped to what is still
   * retained. An empty or fully-evicted range yields a zero-length array, so
   * callers never have to bounds-check.
   */
  read(from: number, to: number): Float32Array
}

export function createRingBuffer(capacity: number): RingBuffer {
  if (capacity <= 0) throw new RangeError('ring capacity must be positive')
  const data = new Float32Array(capacity)
  let total = 0

  function write(frame: Float32Array): void {
    // A frame bigger than the ring can only leave its tail behind anyway;
    // dropping the head up front keeps the copy below to a single pass. The
    // dropped head still advanced the timeline, hence the `dropped` shift.
    const dropped = Math.max(0, frame.length - capacity)
    const src = dropped > 0 ? frame.subarray(dropped) : frame
    const offset = (total + dropped) % capacity
    const firstRun = Math.min(src.length, capacity - offset)
    data.set(src.subarray(0, firstRun), offset)
    if (firstRun < src.length) data.set(src.subarray(firstRun), 0)
    total += frame.length
  }

  function read(from: number, to: number): Float32Array {
    const lo = Math.max(from, total - capacity, 0)
    const hi = Math.min(to, total)
    if (hi <= lo) return new Float32Array(0)
    const out = new Float32Array(hi - lo)
    const start = lo % capacity
    const firstRun = Math.min(out.length, capacity - start)
    out.set(data.subarray(start, start + firstRun), 0)
    if (firstRun < out.length) out.set(data.subarray(0, out.length - firstRun), firstRun)
    return out
  }

  return {
    capacity,
    written: () => total,
    oldest: () => Math.max(0, total - capacity),
    write,
    read,
  }
}

/** Milliseconds → whole samples at `sampleRate`, never negative. */
export function msToSamples(ms: number, sampleRate: number): number {
  return Math.max(0, Math.round((ms / 1000) * sampleRate))
}

/** Samples → milliseconds at `sampleRate`. */
export function samplesToMs(samples: number, sampleRate: number): number {
  return (samples / sampleRate) * 1000
}

/** Root-mean-square of a float frame, i.e. its energy — the VAD's input. */
export function rmsOfFloat32(frame: Float32Array): number {
  if (frame.length === 0) return 0
  let sumSq = 0
  for (let i = 0; i < frame.length; i++) sumSq += frame[i] * frame[i]
  return Math.sqrt(sumSq / frame.length)
}

const WAV_HEADER_BYTES = 44

/**
 * Encode mono float samples as a 16-bit PCM WAV.
 *
 * Uncompressed is the right trade here: a few seconds of 16 kHz mono is well
 * under 200 KB (the proxy's cap is 10 MB), and it costs no encoder, no worker
 * and no container-specific slicing — which is exactly what buying random
 * access to recent audio was about.
 */
export function encodeWav(samples: Float32Array, sampleRate: number): Blob {
  return new Blob([encodeWavBytes(samples, sampleRate)], { type: 'audio/wav' })
}

/** {@link encodeWav}'s payload, before it is wrapped in a `Blob`. */
export function encodeWavBytes(samples: Float32Array, sampleRate: number): ArrayBuffer {
  const buffer = new ArrayBuffer(WAV_HEADER_BYTES + samples.length * 2)
  const view = new DataView(buffer)

  const writeAscii = (offset: number, text: string): void => {
    for (let i = 0; i < text.length; i++) view.setUint8(offset + i, text.charCodeAt(i))
  }

  const byteRate = sampleRate * 2 // mono, 2 bytes per sample
  writeAscii(0, 'RIFF')
  view.setUint32(4, 36 + samples.length * 2, true) // file size minus "RIFF" + size
  writeAscii(8, 'WAVE')
  writeAscii(12, 'fmt ')
  view.setUint32(16, 16, true) // PCM fmt chunk length
  view.setUint16(20, 1, true) // format 1 = PCM
  view.setUint16(22, 1, true) // channels
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, byteRate, true)
  view.setUint16(32, 2, true) // block align
  view.setUint16(34, 16, true) // bits per sample
  writeAscii(36, 'data')
  view.setUint32(40, samples.length * 2, true)

  for (let i = 0; i < samples.length; i++) {
    // Clamp before scaling: a stray |sample| > 1 would otherwise wrap around
    // to the opposite rail and read as a click.
    const s = Math.max(-1, Math.min(1, samples[i]))
    view.setInt16(WAV_HEADER_BYTES + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true)
  }

  return buffer
}
